import type { ComponentType, ReactElement } from 'react';
import { StrictMode } from 'react';
import { StyleSheetManager } from 'styled-components';
import { WidgetAPIProvider } from '@elfsight/widget-js-api';
import { MountingInventory } from '@elfsight/widget-mounting-inventory';
import { createRoot } from 'react-dom/client';
import type { SettingsProviderAPI } from './settings-context';
import { SettingsProvider } from './settings-context';
import type { WidgetSettings } from './widget-settings.interface';

export type WidgetComponent<TSettings extends WidgetSettings> =
  ComponentType<TSettings>;

export async function createWidget(
  container: HTMLElement,
  WidgetComponent: ComponentType,
  render: (widget: ComponentType) => ReactElement = (WidgetComponent) => (
    <WidgetComponent />
  ),
  initialSettings = {}
): Promise<SettingsProviderAPI> {
  return new Promise<SettingsProviderAPI>((resolve, reject) => {
    createRoot(container).render(
      <StrictMode>
        <MountingInventory widgetRoot={container}>
          <WidgetAPIProvider>
            <StyleSheetManager
              shouldForwardProp={(propName) => !propName.startsWith('_')}
            >
              <SettingsProvider
                initialSettings={initialSettings}
                ref={(settingsProviderAPI) => {
                  if (!settingsProviderAPI) {
                    reject('Widget API not initialized');
                    return;
                  }
                  resolve(settingsProviderAPI);
                }}
              >
                {render(WidgetComponent)}
              </SettingsProvider>
            </StyleSheetManager>
          </WidgetAPIProvider>
        </MountingInventory>
      </StrictMode>
    );
  });
}
